/**
 * The ROUTER_REPORT wordmark.
 *
 * Animated, `R_R` fades in and then both remaining runs type out of their R
 * at the same time. A transient cursor trails the second run while it types;
 * without it the separator acts as a cursor for the first word only, so one
 * half reads as typed and the other as letters merely appearing.
 *
 * The separator is the anchor and must not move by so much as a pixel. It is
 * absolutely centred on the wordmark, and the two words hang off its own
 * left and right edges, so nothing either word does -- including the cursor
 * that exists on one side only -- can shift it.
 *
 * An earlier version gave each half a fixed `ch` width inside a flex row and
 * relied on the arithmetic working out centred. It does on paper, and it did
 * in Chrome, but Safari placed the wordmark half a character to the right:
 * exactly the cursor's width, on the side the cursor is. Nothing here now
 * depends on `ch` matching the rendered advance width, on how a partially
 * clipped box resolves, or on inherited alignment.
 *
 * A hidden copy of the finished name sizes the box. Using real text rather
 * than a `ch` estimate means the space reserved is exactly what the final
 * wordmark occupies, in whatever font actually loaded.
 */

import {
  WORDMARK_CURSOR,
  WORDMARK_CURSOR_FADE_MS,
  WORDMARK_CURSOR_FADE_START_MS,
  WORDMARK_FIXED_1,
  WORDMARK_FIXED_2,
  WORDMARK_FULL,
  WORDMARK_GROW_1,
  WORDMARK_GROW_2,
  WORDMARK_SEPARATOR,
  WORDMARK_TYPE_START_MS,
  wordmarkCharDelayMs,
} from "../lib/wordmark.js";

function TypedRun({ text }: { text: string }) {
  return (
    <>
      {[...text].map((character, index) => (
        <span
          key={`${character}-${index}`}
          className="wordmark-char"
          style={{ animationDelay: `${wordmarkCharDelayMs(index)}ms` }}
        >
          {character}
        </span>
      ))}
    </>
  );
}

export function Wordmark({ animated = false, size = 15 }: { animated?: boolean; size?: number }) {
  if (!animated) {
    return <span style={{ fontSize: size, letterSpacing: "0.18em" }}>{WORDMARK_FULL}</span>;
  }

  return (
    <span role="img" aria-label={WORDMARK_FULL} className="wordmark" style={{ fontSize: size }}>
      {/* Reserves exactly the finished name's box, in the real font. */}
      <span aria-hidden="true" className="wordmark-reserve">{WORDMARK_FULL}</span>

      {/* The anchor. Centred on the box; the words hang off its own edges. */}
      <span aria-hidden="true" className="wordmark-sep">
        {WORDMARK_SEPARATOR}
        <span className="wordmark-half wordmark-half-left">
          {WORDMARK_FIXED_1}
          <TypedRun text={WORDMARK_GROW_1} />
        </span>
        <span className="wordmark-half wordmark-half-right">
          {WORDMARK_FIXED_2}
          <TypedRun text={WORDMARK_GROW_2} />
          {/* Two chained animations -- appear, then later fade -- so both
              moments come from the same constants that time the characters. */}
          <span
            className="wordmark-cursor"
            style={{
              animationDelay: `${WORDMARK_TYPE_START_MS}ms, ${WORDMARK_CURSOR_FADE_START_MS}ms`,
              animationDuration: `1ms, ${WORDMARK_CURSOR_FADE_MS}ms`,
            }}
          >
            {WORDMARK_CURSOR}
          </span>
        </span>
      </span>
    </span>
  );
}
